import React, { createContext, ReactNode, useCallback, useContext, useMemo, useReducer } from 'react';
import { contextReducer, InFoViewActions } from './InfoViewReducer';
import { useIntl } from 'react-intl';

export type InfoViewData = {
  error: string;
  data: any;
  message: string;
  loading: boolean;
};

export type InfoViewActions = {
  fetchStart: () => void;
  fetchSuccess: () => void;
  fetchError: (error: string) => void;
  fetchApiError: (data: any) => void;
  showMessage: (message: string) => void;
  clearInfoView: () => void;
};

export const ContextState: InfoViewData = {
  loading: false,
  error: '',
  data: null,
  message: '',
};

const InfoViewContext = createContext<InfoViewData>(ContextState);

export const initialInfoViewActions = {
  fetchStart: () => {},
  fetchSuccess: () => {},
  fetchError: (error: string) => {},
  fetchApiError: (error: any) => {},
  showMessage: (message: string) => {},
  clearInfoView: () => {},
};

const InfoViewActionsContext = createContext<InfoViewActions>(initialInfoViewActions);

export const useInfoViewContext = () => useContext(InfoViewContext);
export const useInfoViewActionsContext = () => useContext(InfoViewActionsContext);

type InfoViewContextProviderProps = {
  children: ReactNode;
};
const InfoViewContextProvider: React.FC<InfoViewContextProviderProps> = (props) => {
  const [state, dispatch] = useReducer(contextReducer, ContextState, () => ContextState);
  const { messages } = useIntl();
  const fetchStart = useCallback(() => {
    dispatch({ type: InFoViewActions.FETCH_STARTS });
  }, []);

  const fetchSuccess = useCallback(() => {
    dispatch({ type: InFoViewActions.FETCH_SUCCESS });
  }, []);

  const fetchError = useCallback((error: string) => {
    dispatch({ type: InFoViewActions.SET_ERROR, payload: error });
  }, []);

  const fetchApiError = useCallback((error: any) => {
    const systemError = error?.response?.data?.errorKey;

    const key = systemError ? `api.error.${systemError}` : 'common.toast.fail';
    dispatch({
      type: InFoViewActions.SET_ERROR,
      payload: String(messages[key]),
    });
  }, [messages]);

  const showMessage = useCallback((message: string) => {
    dispatch({ type: InFoViewActions.SET_MESSAGE, payload: message });
  }, []);

  const clearInfoView = useCallback(() => {
    dispatch({ type: InFoViewActions.CLEAR_INFOVIEW });
  }, []);

  const infoViewActions = useMemo(
    () => ({
      fetchStart,
      fetchSuccess,
      fetchError,
      fetchApiError,
      showMessage,
      clearInfoView,
    }),
    [fetchStart, fetchSuccess, fetchError, fetchApiError, showMessage, clearInfoView],
  );

  return (
    <InfoViewContext.Provider value={state}>
      <InfoViewActionsContext.Provider value={infoViewActions}>
        {props.children}
      </InfoViewActionsContext.Provider>
    </InfoViewContext.Provider>
  );
};

export default InfoViewContextProvider;
